import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { specialList } from "@/mocks/specials";

const navItems = [
  { label: "首页", to: "/" },
  { label: "动画推荐", to: "/category/recommend" },
  { label: "动漫新闻", to: "/category/news" },
  { label: "角色介绍", to: "/category/characters" },
];

export default function Header() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [specialOpen, setSpecialOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const query = keyword.trim();
    if (!query) return;
    setMobileOpen(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "bg-primary-100 text-primary-800"
        : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="hidden border-b border-background-100 bg-secondary-50 md:block">
        <div className="mx-auto flex h-8 max-w-[1280px] items-center justify-between px-4 text-xs text-secondary-900 md:px-6">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-megaphone-line"></i>
            风车动漫网 · 优质动漫资源与最新动漫资讯分享平台
          </span>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1">
              <i className="ri-heart-3-line"></i>
              收藏内容仅本地演示
            </span>
            <Link to="/about" className="cursor-pointer hover:text-secondary-700">
              关于我们
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex flex-none cursor-pointer items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-windy-line text-2xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-xl tracking-wide text-foreground-950">
              风车动漫网
            </span>
            <span className="mt-0.5 text-[10px] tracking-[0.2em] text-primary-600">
              WINDMILL ANIME
            </span>
          </span>
        </Link>

        <nav className="ml-2 hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
              {item.label}
            </NavLink>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setSpecialOpen(true)}
            onMouseLeave={() => setSpecialOpen(false)}
          >
            <button
              type="button"
              onClick={() => setSpecialOpen((prev) => !prev)}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              专题
              <i className="ri-arrow-down-s-line text-base"></i>
            </button>
            {specialOpen && (
              <div className="absolute left-0 top-full w-72 pt-2">
                <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5">
                  {specialList.map((special) => (
                    <Link
                      key={special.id}
                      to={`/special/${special.id}`}
                      onClick={() => setSpecialOpen(false)}
                      className="flex cursor-pointer items-start gap-2 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-800"
                    >
                      <i className="ri-bookmark-3-line mt-0.5 text-accent-500"></i>
                      <span className="line-clamp-1">{special.title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden lg:block">
          <div className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索动画 / 新闻 / 角色"
              className="h-10 w-56 rounded-full border border-background-300 bg-background-100 pl-9 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </form>

        <Link
          to="/search"
          className="hidden h-10 w-10 flex-none cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700 lg:hidden md:flex"
          aria-label="搜索"
        >
          <i className="ri-search-line text-lg"></i>
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden"
          aria-label="打开菜单"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={submitSearch} className="mb-3">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索动画 / 新闻 / 角色"
                className="h-10 w-full rounded-full border border-background-300 bg-background-100 pl-9 pr-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to={`/special/${specialList[0].id}`}
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              专题
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              关于我们
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}